<template>
  <div class="recipe-list">
    <div class="filters-section">
      <div class="search-box">
        <input type="text" v-model="searchQuery" placeholder="Search recipes..." @input="debouncedSearch" />
      </div>

      <div class="filter-row">
        <div class="filter-group">
          <label>Meal Type</label>
          <select v-model="filters.mealType" @change="applyFilters">
            <option value="">All</option>
            <option v-for="type in mealTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Category</label>
          <select v-model="filters.category" @change="applyFilters">
            <option value="">All</option>
            <option v-for="cat in categories" :key="cat.value" :value="cat.value">
              {{ cat.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="dietary-filters">
        <span class="filter-label">Dietary:</span>
        <button v-for="tag in dietaryTags" :key="tag.value" class="dietary-filter-btn"
          :class="{ active: filters.dietary.includes(tag.value) }" @click="toggleDietaryFilter(tag.value)">
          {{ tag.label }}
        </button>
      </div>

      <div class="quick-filters">
        <button class="quick-filter-btn" :class="{ active: filters.favoritesOnly }" @click="toggleFavoritesOnly">
          <span class="star-icon">&#9733;</span> Favorites Only
        </button>
        <button v-if="hasActiveFilters" class="clear-filters-btn" @click="clearFilters">
          Clear Filters
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading recipes...</p>
    </div>

    <div v-else-if="!recipes.length" class="empty-state">
      <p>No recipes found matching your filters</p>
      <button class="clear-filters-btn" @click="clearFilters">
        Clear Filters
      </button>
    </div>

    <div v-else class="recipes-grid">
      <RecipeCard v-for="recipe in recipes" :key="recipe.id" :recipe="recipe" :userId="userId"
        @favorite-toggled="handleFavoriteToggled" @quick-add="handleQuickAdd" />
    </div>

    <div v-if="recipes.length && recipes.length >= 20" class="load-more">
      <button @click="loadMore" :disabled="loadingMore">
        {{ loadingMore ? "Loading..." : "Load More" }}
      </button>
    </div>
  </div>
</template>

<script>
import Nutrition from "@/services/nutrition-service";
import RecipeCard from "./RecipeCard.vue";

export default {
  name: "RecipeList",

  components: {
    RecipeCard,
  },

  props: {
    userId: {
      type: [Number, String],
      default: null,
    },
    initialContext: {
      type: String,
      default: null,
    },
    initialMealType: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      recipes: [],
      loading: false,
      loadingMore: false,
      searchQuery: "",
      searchTimeout: null,
      filters: {
        mealType: this.initialMealType || "",
        category: "",
        dietary: [],
        favoritesOnly: false,
        context: this.initialContext || "",
      },
      mealTypes: [],
      categories: [],
      dietaryTags: [],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.searchQuery ||
        this.filters.mealType ||
        this.filters.category ||
        this.filters.dietary.length > 0 ||
        this.filters.favoritesOnly
      );
    },
  },

  async mounted() {
    this.mealTypes = Nutrition.getAllMealTypes();
    this.categories = Nutrition.getCategories();
    this.dietaryTags = Nutrition.getDietaryTags();
    await this.loadRecipes();
  },

  methods: {
    async loadRecipes() {
      this.loading = true;
      try {
        const response = await Nutrition.getFilteredTemplates({
          context: this.filters.context || null,
          mealType: this.filters.mealType || null,
          category: this.filters.category || null,
          dietary: this.filters.dietary,
          favoritesOnly: this.filters.favoritesOnly,
          search: this.searchQuery || null,
          userId: this.userId,
        });

        if (response.data && response.data.success) {
          this.recipes = response.data.data || [];
        }
      } catch (error) {
        console.error("Error loading recipes:", error);
        this.recipes = [];
      } finally {
        this.loading = false;
      }
    },

    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.loadRecipes();
      }, 300);
    },

    applyFilters() {
      this.loadRecipes();
    },

    toggleDietaryFilter(tag) {
      const index = this.filters.dietary.indexOf(tag);
      if (index > -1) {
        this.filters.dietary.splice(index, 1);
      } else {
        this.filters.dietary.push(tag);
      }
      this.loadRecipes();
    },

    toggleFavoritesOnly() {
      this.filters.favoritesOnly = !this.filters.favoritesOnly;
      this.loadRecipes();
    },

    clearFilters() {
      this.searchQuery = "";
      this.filters = {
        mealType: "",
        category: "",
        dietary: [],
        favoritesOnly: false,
        context: "",
      };
      this.loadRecipes();
    },

    async loadMore() {
      // Pagination would be implemented in backend
      this.loadingMore = true;
      // For now, just reload
      await this.loadRecipes();
      this.loadingMore = false;
    },

    handleFavoriteToggled({ templateId, favorited }) {
      const recipe = this.recipes.find((r) => r.id === templateId);
      if (recipe) {
        recipe.is_favorite = favorited ? 1 : 0;
      }
      // If favorites only is active and item was unfavorited, remove it
      if (this.filters.favoritesOnly && !favorited) {
        this.recipes = this.recipes.filter((r) => r.id !== templateId);
      }
    },

    handleQuickAdd(recipe) {
      this.$emit("quick-add", recipe);
    },
  },
};
</script>

<style lang="scss" scoped>
.recipe-list {
  padding: 16px;
}

.filters-section {
  margin-bottom: 20px;
}

.search-box {
  margin-bottom: 12px;

  input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.95rem;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #007bff;
    }
  }
}

.filter-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.filter-group {
  flex: 1;

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #666;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9rem;
    background: white;
    cursor: pointer;
    outline: none;

    &:focus {
      border-color: #007bff;
    }
  }
}

.dietary-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.filter-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
}

.dietary-filter-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 16px;
  background: white;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #1f7e34;

  &:hover {
    border-color: #007bff;
    color: #007bff;
  }

  &.active {
    background: #007bff;
    border-color: #007bff;
    color: white;
  }
}

.quick-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.quick-filter-btn {
  padding: 8px 14px;
  border: 1px solid #ffc107;
  color: #ffc107;
  border-radius: 6px;
  background: white;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;

  .star-icon {
    color: #ffc107;
  }

  &:hover {
    background: #fff3cd;
  }

  &.active {
    background: #ffc107;
    color: #212529;

    .star-icon {
      color: #212529;
    }
  }
}

.clear-filters-btn {
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  background: #f8f9fa;
  font-size: 0.85rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
    color: #333;
  }
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #007bff;
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.recipes-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.load-more {
  text-align: center;
  margin-top: 20px;

  button {
    padding: 12px 24px;
    border: 1px solid #007bff;
    border-radius: 8px;
    background: white;
    color: #007bff;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: #007bff;
      color: white;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
